import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Loader2, LogOut, Trash2, ArrowLeft, Inbox, RefreshCw } from "lucide-react";

const STATUSES = ["Nuovo", "Contattato", "Incontro fissato", "Archiviato"];

const STATUS_STYLE = {
  "Nuovo": "bg-goldlight text-golddeep border-gold/40",
  "Contattato": "bg-sand text-ink border-ink/15",
  "Incontro fissato": "bg-ink text-paper border-ink",
  "Archiviato": "bg-white text-mist border-ink/10",
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("oic_esclusa", "1");
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Accesso non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-mist/60 focus-gold";

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="admin-login-modal"
        className="w-full max-w-md bg-white border border-ink/10 rounded-3xl p-8 sm:p-10 shadow-[0_30px_60px_-40px_rgba(20,41,46,0.4)]">
        <div className="flex flex-col items-center text-center mb-8">
          <img src="/assets/logo.png" alt="Oltre il Conflitto" className="h-20 w-20 rounded-full ring-1 ring-ink/10 object-cover mb-4" />
          <h1 className="font-serif font-semibold text-ink text-2xl">Area riservata</h1>
          <p className="text-sm text-mist mt-1">Accesso per la Dott.ssa Maritato</p>
        </div>
        <label className="block text-sm font-semibold text-ink mb-2" htmlFor="admin-email">Email</label>
        <input id="admin-email" type="email" required data-testid="admin-email-input"
          value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@oltreilconflitto.it" className={inputCls} />
        <label className="block text-sm font-semibold text-ink mb-2 mt-5" htmlFor="admin-password">Password</label>
        <input id="admin-password" type="password" required data-testid="admin-password-input"
          value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} />
        {error && <p data-testid="admin-login-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper font-semibold px-6 py-3.5 hover:bg-night active:scale-95 disabled:opacity-60 transition-all duration-300">
          {loading && <Loader2 size={16} className="animate-spin" />}
          Accedi
        </button>
        <Link to="/" className="mt-5 inline-flex items-center gap-2 text-sm text-mist hover:text-golddeep transition-colors" data-testid="admin-back-home">
          <ArrowLeft size={14} /> Torna al sito
        </Link>
      </form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Tutti");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [{ data }, statsRes] = await Promise.all([
        api.get("/contact-requests"),
        api.get("/stats"),
      ]);
      setItems(data);
      setVisits(statsRes.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Errore nel caricamento."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    const prev = items;
    setItems((arr) => arr.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await api.patch(`/contact-requests/${id}`, { status });
    } catch {
      setItems(prev);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa richiesta?")) return;
    try {
      await api.delete(`/contact-requests/${id}`);
      setItems((arr) => arr.filter((r) => r.id !== id));
    } catch { /* ignore */ }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    onLogout();
  };

  const visible = filter === "Tutti" ? items : items.filter((r) => r.status === filter);
  const counts = Object.fromEntries(STATUSES.map((s) => [s, items.filter((r) => r.status === s).length]));

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard-container">
      <header className="bg-night text-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Oltre il Conflitto" className="h-10 w-10 rounded-full ring-1 ring-gold/40 bg-paper object-cover" />
            <div>
              <p className="font-serif font-semibold tracking-wide text-sm sm:text-base">OLTRE IL CONFLITTO</p>
              <p className="text-[11px] text-paper/60">Area riservata · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm text-paper/70 hover:text-gold px-3 py-2 transition-colors" data-testid="admin-view-site">
              <ArrowLeft size={15} /> Vedi il sito
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-paper/25 text-sm px-4 py-2 hover:border-gold hover:text-gold transition-colors">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="font-serif font-semibold text-ink text-2xl sm:text-3xl">Richieste di contatto</h1>
          <button onClick={load} data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink text-sm font-semibold px-4 py-2 hover:border-gold hover:text-golddeep transition-colors">
            <RefreshCw size={14} /> Aggiorna
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <div className="bg-white border border-ink/10 rounded-2xl p-5">
            <p className="font-serif text-3xl font-semibold text-ink">{items.length}</p>
            <p className="text-xs text-mist mt-1">Richieste totali</p>
          </div>
          {STATUSES.slice(0, 3).map((s) => (
            <div key={s} className="bg-white border border-ink/10 rounded-2xl p-5">
              <p className="font-serif text-3xl font-semibold text-golddeep">{counts[s]}</p>
              <p className="text-xs text-mist mt-1">{s}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-filters">
          {["Tutti", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s.replace(/\s/g, "-").toLowerCase()}`}
              className={`rounded-full text-sm font-semibold px-4 py-2 border transition-colors duration-300 ${
                filter === s ? "bg-ink text-paper border-ink" : "bg-white text-ink/70 border-ink/15 hover:border-gold hover:text-golddeep"
              }`}>
              {s}
            </button>
          ))}
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6" data-testid="admin-error">{error}</p>}

        {visits && (
          <div className="mb-10" data-testid="admin-visits-panel">
            <h2 className="font-serif font-semibold text-ink text-xl mb-4">Visite del sito</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
              {[
                ["Oggi", visits.today],
                ["Ultimi 7 giorni", visits.last7],
                ["Ultimi 30 giorni", visits.last30],
                ["Totale", visits.total],
                ["Download brochure", visits.downloads],
              ].map(([label, val]) => (
                <div key={label} className="bg-white border border-ink/10 rounded-2xl p-5">
                  <p className="font-serif text-3xl font-semibold text-ink">{val}</p>
                  <p className="text-xs text-mist mt-1">{label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-ink/10 rounded-2xl p-5" data-testid="admin-visits-chart">
                <p className="font-display text-[11px] uppercase tracking-[0.18em] text-mist font-semibold mb-4">Ultime 2 settimane</p>
                <div className="flex items-end gap-1.5 h-24">
                  {visits.daily.map((d) => {
                    const max = Math.max(...visits.daily.map((x) => x.count), 1);
                    return (
                      <div key={d.date} className="flex-1 h-full flex flex-col items-center justify-end gap-1">
                        <div className="w-full rounded-t bg-ink/85 hover:bg-gold transition-colors"
                          style={{ height: `${Math.max((d.count / max) * 100, d.count > 0 ? 8 : 3)}%` }}
                          title={`${d.date}: ${d.count} visite`} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[10px] text-mist mt-2">
                  <span>{visits.daily[0]?.date.slice(5).split("-").reverse().join("/")}</span>
                  <span>oggi</span>
                </div>
              </div>
              <div className="bg-white border border-ink/10 rounded-2xl p-5" data-testid="admin-visits-channels">
                <p className="font-display text-[11px] uppercase tracking-[0.18em] text-mist font-semibold mb-4">Da dove arrivano</p>
                {visits.channels.length === 0 ? (
                  <p className="text-sm text-mist">Ancora nessun dato.</p>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {visits.channels.map((c) => {
                      const max = visits.channels[0].count;
                      return (
                        <div key={c.name}>
                          <div className="flex justify-between text-sm text-ink mb-1">
                            <span>{c.name}</span><span className="font-semibold">{c.count}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-sand overflow-hidden">
                            <div className="h-full rounded-full bg-gold" style={{ width: `${(c.count / max) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <p className="text-[11px] text-mist mt-3">Statistiche anonime: niente cookie, nessun dato personale dei visitatori.</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-golddeep" size={28} /></div>
        ) : visible.length === 0 ? (
          <div className="bg-white border border-ink/10 rounded-2xl py-16 flex flex-col items-center text-center" data-testid="admin-empty-state">
            <Inbox size={36} className="text-mist mb-3" />
            <p className="text-mist text-sm">Nessuna richiesta {filter !== "Tutti" ? `con stato "${filter}"` : "per ora"}.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {visible.map((r) => (
              <article key={r.id} data-testid="admin-lead-row"
                className="bg-white border border-ink/10 rounded-2xl p-5 sm:p-6 hover:border-gold/50 transition-colors duration-300">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif font-semibold text-ink text-lg">{r.name}</h3>
                      <span className={`text-[11px] font-semibold uppercase tracking-wider border rounded-full px-3 py-1 ${STATUS_STYLE[r.status] || STATUS_STYLE["Nuovo"]}`}>
                        {r.status}
                      </span>
                    </div>
                    <p className="text-xs text-mist mt-1">
                      {new Date(r.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })} · {r.request_type}
                    </p>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-ink/80">
                      <a href={`mailto:${r.email}`} className="hover:text-golddeep transition-colors">{r.email}</a>
                      {r.phone && <a href={`tel:${r.phone}`} className="hover:text-golddeep transition-colors">{r.phone}</a>}
                    </div>
                    <p className="mt-3 text-sm text-mist leading-relaxed whitespace-pre-wrap">{r.message}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                      data-testid={`admin-status-select-${r.id}`}
                      className="rounded-full border border-ink/15 bg-white text-sm px-3 py-2 text-ink focus-gold">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => remove(r.id)} data-testid={`admin-delete-${r.id}`} aria-label="Elimina richiesta"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full text-mist hover:text-red-700 hover:bg-red-50 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false));
  }, []);

  if (user === null) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 className="animate-spin text-golddeep" size={28} />
      </div>
    );
  }
  if (user === false) return <LoginForm onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => setUser(false)} />;
}
